import api from './api';
import { Transfer, Provider, User } from '../types';

export interface ProviderPayment {
  id: string;
  transferId: string;
  providerId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  notes?: string;
  createdAt: string;
  transfer?: Pick<Transfer, 'id' | 'transferCode' | 'transferDate' | 'pickupLocation' | 'dropoffLocation'>;
  provider?: Pick<Provider, 'id' | 'name'>;
  recordedBy?: Pick<User, 'id' | 'fullName' | 'email'>;
}

export interface CreateProviderPaymentDto {
  transferId?: string;
  providerId: string;
  amount: number;
  paymentDate: string;
  paymentMethod?: string;
  notes?: string;
}

export interface ProviderPaymentSummary {
  providerId: string;
  providerName: string;
  totalAmount: number;
  totalPaid: number;
  totalPending: number;
  transferCount: number;
  paidTransferCount: number;
  pendingTransferCount: number;
  paymentMode?: string;
  balance?: number;
}

export interface TransferPaymentDetails {
  transfer: {
    id: string;
    transferCode?: string;
    transferDate: string;
    pickupLocation: string;
    dropoffLocation: string;
    providerPrice: number;
    providerName: string;
  };
  totalPrice: number;
  totalPaid: number;
  remaining: number;
  payments: ProviderPayment[];
}

export interface TransferPaymentRow {
  id: string;
  transferCode: string;
  transferDate: string;
  transferTime: string;
  pickupLocation: string;
  dropoffLocation: string;
  status: string;
  providerPrice: number;
  totalPaid: number;
  remaining: number;
  isPaid: boolean;
}

class ProviderPaymentsService {
  async create(data: CreateProviderPaymentDto): Promise<ProviderPayment> {
    const response = await api.post('/provider-payments', data);
    return response.data;
  }

  async getAll(filters?: {
    providerId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ProviderPayment[]> {
    const params = new URLSearchParams();
    if (filters?.providerId) params.append('providerId', filters.providerId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/provider-payments?${params.toString()}`);
    return response.data;
  }

  async getSummary(filters?: {
    providerId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ProviderPaymentSummary[]> {
    const params = new URLSearchParams();
    if (filters?.providerId) params.append('providerId', filters.providerId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/provider-payments/summary?${params.toString()}`);
    return response.data;
  }

  async getUnpaidTransfers(providerId: string): Promise<TransferPaymentRow[]> {
    const response = await api.get(`/provider-payments/unpaid-transfers/${providerId}`);
    return response.data;
  }

  async getTransferDetails(transferId: string): Promise<TransferPaymentDetails> {
    const response = await api.get(`/provider-payments/transfer/${transferId}`);
    return response.data;
  }

  async getMyTransfers(): Promise<TransferPaymentRow[]> {
    const response = await api.get('/provider-payments/my-transfers');
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/provider-payments/${id}`);
  }
}

export const providerPaymentsService = new ProviderPaymentsService();
